import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useTags from '../hooks/useTags';
import { apiCall } from '../utils/api';
import useUserPermissions from '../hooks/useUserPermissions';

function EditRunbook() {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const { canModify, isReadOnly, isLoading } = useUserPermissions();  const [runbookId, setRunbookId] = useState('');
  const [runbookTitle, setRunbookTitle] = useState('');
  const [tribe, setTribe] = useState('');
  const [solution, setSolution] = useState('');
  const [modules, setModules] = useState([]);
  const [modifiedBy, setModifiedBy] = useState('');
  const [removedModules, setRemovedModules] = useState([]);

  const { selectedTags, setSelectedTags, tagInput, suggestions, tagInputRef, suggestionBoxRef, addTag, removeTag, handleTagInputChange, handleTagInputKeyPress, handleTagInputBlur } = useTags();

  // Redirect users without modify permissions
  useEffect(() => {
    if (!isLoading && !canModify()) {
      alert('You do not have permission to edit runbooks. Redirecting to view mode.');
      navigate(`/view-runbook/${id}`);
      return;
    }
  }, [canModify, isLoading, navigate, id]);

  useEffect(() => {
    const fetchRunbookData = async () => {
      try {
        const data = await apiCall(`/runbooks/${id}`);
        setRunbookId(data['Runbook ID'] || '');
        setRunbookTitle(data['Runbook Title'] || '');
        setTribe(data.Tribe || '');
        setSolution(data.Solution || '');
        setModules(data.Modules || []);
        setModifiedBy(data['ModifiedBy'] || '');
        setSelectedTags(data.Tags || []);
      } catch (error) {
        console.error('Error fetching runbook data:', error);
        // Set default values to prevent undefined errors
        setModules([]);
        setSelectedTags([]);
      }
    };
    fetchRunbookData();
  }, [id, setSelectedTags]);

  const handleAddModuleClick = () => {
    navigate('/add_module', { state: { runbook_id: runbookId } });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      runbookTitle,
      modifiedBy,
      removedModules,
      selectedTags: selectedTags ? Array.from(selectedTags) : [],
      // also include selectedTags etc.
    };
    
    try {
      await apiCall(`/runbooks/${runbookId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      alert('Runbook updated successfully!');
      navigate('/runbook_list');
    } catch (error) {
      console.error('Error updating runbook:', error);
      alert('Failed to update runbook.');
    }
  };

  const handleRemoveModule = (moduleIdToRemove) => {
    setModules((prevModules) =>
      prevModules.filter((module) => module['Module ID'] !== moduleIdToRemove)
    );
    setRemovedModules((prevRemoved) => [...prevRemoved, moduleIdToRemove]);
  };

  return (
    <div className="container mt-4">
      <form onSubmit={handleSubmit}>
        <label>Runbook ID:</label>
        <input
          type="text"
          name="runbook_id"
          value={runbookId}
          readOnly
          className="form-control"
          style={{ backgroundColor: '#d6d6d6', color: '#333' }}
        />

        <label>Runbook Title:</label>
        <input
          type="text"
          name="runbook_title"
          value={runbookTitle}
          onChange={(e) => setRunbookTitle(e.target.value)}
          className="form-control"
          required
        />

        <label>Tribe:</label>
        <input
          type="text"
          name="tribe"
          value={tribe}
          readOnly
          className="form-control"
          style={{ backgroundColor: '#d6d6d6', color: '#333' }}
        />

        <label>Solution:</label>
        <input
          type="text"
          name="solution"
          value={solution}
          readOnly
          className="form-control"
          style={{ backgroundColor: '#d6d6d6', color: '#333' }}
        />

        {modules && modules.length > 0 && (
          <>
            <label>Existing Modules:</label>
            <table className="table table-bordered">
              <thead className="table-dark">
                <tr>
                  <th>Module ID</th>
                  <th>Module Description</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {modules.map((moduleItem) => (
                  <tr key={moduleItem['Module ID']}>
                    <td>{moduleItem['Module ID']}</td>
                    <td>{moduleItem['Module Description']}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => handleRemoveModule(moduleItem['Module ID'])}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <label htmlFor="tags">Tags:</label>
        <div id="selected-tags" className="tag-container">
          {selectedTags && selectedTags.map((tag) => (
            <span key={tag} className="tag">
              {tag} <span className="remove-tag" onClick={() => removeTag(tag)}>✖</span>
            </span>
          ))}
        </div>
        <input
          type="text"
          id="tags"
          name="tags"
          placeholder="Enter tags"
          value={tagInput}
          onChange={handleTagInputChange}
          onKeyPress={handleTagInputKeyPress}
          onBlur={handleTagInputBlur}
          ref={tagInputRef}
        />
        {suggestions && suggestions.length > 0 && (
          <div id="tag-suggestions" className="suggestions" ref={suggestionBoxRef}>
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                className="suggestion-item"
                onClick={() => addTag(suggestion)}
              >
                {suggestion}
              </div>
            ))}
          </div>
        )}

        <label>Modified By:</label>
        <input
          type="text"
          name="modified_by"
          value={modifiedBy}
          onChange={(e) => setModifiedBy(e.target.value)}
          className="form-control"
          required
          disabled={isReadOnly()}
        />

        <div className="d-flex gap-2 mt-3">
          {canModify() && (
            <>
              <button id="addModuleBtn" type="button" className="btn btn-success" onClick={handleAddModuleClick}>Add Module</button>
              <button type="submit" className="btn btn-primary" id="submitButton">Submit</button>
            </>
          )}
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/runbook_list')}>
            {canModify() ? 'Cancel' : 'Back'}
          </button>
        </div>
      </form>
    </div>
  );

}

export default EditRunbook;


